// "Import from Teams" dialog (phase 4 of the call-flows plan). The admin
// downloads the read-only PowerShell export script, runs it as a Teams
// Administrator, and uploads the resulting JSON here. The backend maps it to
// proposals; each one is reviewed in the normal editor before saving, so
// nothing lands unreviewed.

import { useRef, useState } from 'react';
import { X, Download, Upload, AlertCircle, Check, Pencil, MapPin, AlertTriangle, Music } from 'lucide-react';
import { api, ImportProposal, ImportResult, ExportAudioFile } from '../api/client';
import { matchAudioFiles } from './callFlowEditor/audioMatch';

interface CallFlowImportProps {
  savedIds: Set<string>;
  accentColor: string;
  onReview: (proposal: ImportProposal) => void;
  /** Fired after recordings from the export's audio folder were stored. */
  onAudioUploaded?: () => Promise<void> | void;
  onClose: () => void;
}

type AudioUploadState = { id: string; fileName: string; status: 'pending' | 'uploading' | 'done' | 'error'; message?: string };

type Phase =
  | { kind: 'pick' }
  | { kind: 'mapping' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; result: ImportResult };

export function CallFlowImport({ savedIds, accentColor, onReview, onAudioUploaded, onClose }: CallFlowImportProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'pick' });
  const fileRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLInputElement>(null);
  const [audioUploads, setAudioUploads] = useState<AudioUploadState[]>([]);
  const [audioNote, setAudioNote] = useState<string | null>(null);

  async function handleAudioFiles(files: File[], manifest: ExportAudioFile[]) {
    const matched = matchAudioFiles(files, manifest);
    const unmatched = matched.filter(m => !m.entry).map(m => m.file.name);
    setAudioNote(unmatched.length ? `Not part of this export (skipped): ${unmatched.join(', ')}` : null);
    const todo = matched.filter((m): m is { file: File; entry: ExportAudioFile } => !!m.entry);
    setAudioUploads(prev => {
      const rest = prev.filter(p => !todo.some(t => t.entry.id === p.id));
      return [...rest, ...todo.map(t => ({ id: t.entry.id, fileName: t.entry.fileName || t.file.name, status: 'pending' as const }))];
    });
    for (const { file, entry } of todo) {
      setAudioUploads(prev => prev.map(p => (p.id === entry.id ? { ...p, status: 'uploading' } : p)));
      try {
        await api.uploadCallFlowAudio(file, { teamsAudioFileId: entry.id, teamsApplicationId: entry.applicationId, fileName: entry.fileName || file.name });
        setAudioUploads(prev => prev.map(p => (p.id === entry.id ? { ...p, status: 'done' } : p)));
      } catch (e: any) {
        setAudioUploads(prev => prev.map(p => (p.id === entry.id ? { ...p, status: 'error', message: e?.message || 'Upload failed' } : p)));
      }
    }
    if (todo.length && onAudioUploaded) await onAudioUploaded();
  }

  async function handleFile(file: File) {
    setPhase({ kind: 'mapping' });
    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        setPhase({ kind: 'error', message: 'That file is not valid JSON — upload the file produced by the export script.' });
        return;
      }
      const result = await api.importTeamsCallFlows(parsed);
      setPhase({ kind: 'ready', result });
    } catch (e: any) {
      setPhase({ kind: 'error', message: e?.message || 'Import failed' });
    }
  }

  function ProposalRow({ p }: { p: ImportProposal }) {
    const saved = savedIds.has(p.locationId);
    const otherWarnings = p.warnings.filter(w => !p.lossyReasons.includes(w));
    return (
      <div className="px-4 py-3 border-b border-gray-100 last:border-b-0 flex items-start gap-3">
        <MapPin className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 flex items-center gap-2 flex-wrap">
            {p.locationName}
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${p.existing ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
              {p.existing ? 'update' : 'new'}
            </span>
            {p.nameOverridden && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-medium"
                title={`Teams name: ${p.draft.source?.autoAttendantName}`}>
                your name kept
              </span>
            )}
            {p.lossy && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-medium inline-flex items-center gap-1"
                title="Some of the Teams configuration could not be represented — review before saving">
                <AlertTriangle className="w-3 h-3" />needs review
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {p.draft.mainNumbers[0]} · {p.draft.nodes.length} nodes
            {p.warnings.length > 0 && <span className="text-amber-600"> · {p.warnings.length} warning{p.warnings.length === 1 ? '' : 's'}</span>}
          </div>
          {p.lossyReasons.length > 0 && (
            <ul className="mt-1 text-[11px] text-red-700 list-disc pl-4 space-y-0.5">
              {p.lossyReasons.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
              {p.lossyReasons.length > 5 && <li>… {p.lossyReasons.length - 5} more</li>}
            </ul>
          )}
          {otherWarnings.length > 0 && (
            <ul className="mt-1 text-[11px] text-amber-700 list-disc pl-4 space-y-0.5">
              {otherWarnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
              {otherWarnings.length > 5 && <li>… {otherWarnings.length - 5} more</li>}
            </ul>
          )}
        </div>
        {saved ? (
          <span className="text-xs text-green-700 flex items-center gap-1 shrink-0 mt-1"><Check className="w-3.5 h-3.5" />Saved</span>
        ) : (
          <button onClick={() => onReview(p)}
            className="px-2.5 py-1.5 text-xs text-white rounded font-medium flex items-center gap-1.5 shrink-0"
            style={{ backgroundColor: accentColor }}>
            <Pencil className="w-3 h-3" />Review & save
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-40 overflow-y-auto" onClick={onClose}>
      <div className="max-w-2xl mx-auto my-10 bg-white rounded-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Import from Teams</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5">
          {(phase.kind === 'pick' || phase.kind === 'error' || phase.kind === 'mapping') && (
            <>
              <ol className="text-sm text-gray-700 space-y-3 mb-5 list-decimal pl-5">
                <li>
                  Download the read-only export script and run it in PowerShell 7 as a <span className="font-medium">Teams Administrator</span> of the tenant:{' '}
                  <a href="/export-teams-callflows.ps1" download
                    className="inline-flex items-center gap-1 text-purple-700 underline">
                    <Download className="w-3.5 h-3.5" />export-teams-callflows.ps1
                  </a>
                  <div className="text-xs text-gray-500 mt-1 font-mono">Install-Module MicrosoftTeams; ./export-teams-callflows.ps1 -IncludeAudio</div>
                  <div className="text-xs text-gray-500 mt-1"><span className="font-mono">-IncludeAudio</span> also downloads the greetings, menu prompts and hold music so people can listen to them in the phone-menu guide.</div>
                </li>
                <li>Upload the <span className="font-mono text-xs">teams-callflows-export.json</span> it produces. One location is proposed per top-level auto attendant; menus reached from another menu are part of their parent.</li>
                <li>Review each proposal in the editor — assign viewer groups, fix anything flagged — and save.</li>
              </ol>
              <p className="text-xs text-gray-500 mb-4">
                Re-importing later refreshes the flows. Locations you've renamed keep your name; viewer groups, guide display names and chosen audio are kept too.
              </p>
              <input ref={fileRef} type="file" accept=".json,application/json" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
              <button onClick={() => fileRef.current?.click()} disabled={phase.kind === 'mapping'}
                className="w-full px-4 py-3 text-sm border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50/30 text-gray-600 flex items-center justify-center gap-2 disabled:opacity-60">
                <Upload className="w-4 h-4" />
                {phase.kind === 'mapping' ? 'Mapping export…' : 'Upload export JSON'}
              </button>
              {phase.kind === 'error' && (
                <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{phase.message}
                </div>
              )}
            </>
          )}

          {phase.kind === 'ready' && (() => {
            const { proposals, warnings, audioFiles } = phase.result;
            const top = proposals.filter(p => !p.nested);
            const nested = proposals.filter(p => p.nested);
            return (
              <>
                {warnings.map((w, i) => (
                  <div key={i} className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-start gap-2">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{w}
                  </div>
                ))}
                {audioFiles.length > 0 && (() => {
                  const done = audioUploads.filter(u => u.status === 'done').length;
                  return (
                    <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
                      <div className="flex items-start gap-2">
                        <Music className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">Step 2 — add the recordings ({done}/{audioFiles.length} stored)</div>
                          <div className="text-blue-800">The export lists {audioFiles.length} audio file{audioFiles.length === 1 ? '' : 's'} (greetings, menu prompts, hold music). Select the files from the <span className="font-mono">audio</span> folder next to the JSON; they are matched to the prompts automatically so people can play them in the guide.</div>
                          <input ref={audioRef} type="file" multiple accept="audio/*,.wav,.mp3" className="hidden"
                            onChange={e => { const fs = Array.from(e.target.files || []); if (fs.length) handleAudioFiles(fs, audioFiles); e.target.value = ''; }} />
                          <button onClick={() => audioRef.current?.click()}
                            className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-blue-300 rounded bg-white hover:bg-blue-50 text-blue-900">
                            <Upload className="w-3.5 h-3.5" />Select audio files
                          </button>
                          {audioNote && <div className="mt-1 text-amber-700">{audioNote}</div>}
                          {audioUploads.length > 0 && (
                            <ul className="mt-2 space-y-0.5">
                              {audioUploads.map(u => (
                                <li key={u.id} className="flex items-center gap-2">
                                  <span className={`w-1.5 h-1.5 rounded-full ${u.status === 'done' ? 'bg-green-500' : u.status === 'error' ? 'bg-red-500' : 'bg-blue-400 animate-pulse'}`} />
                                  <span className="truncate">{u.fileName}</span>
                                  <span className="text-blue-700/70">{u.status === 'done' ? 'stored' : u.status === 'error' ? u.message : u.status}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {proposals.length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-500">Nothing to import.</div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {top.map(p => <ProposalRow key={p.locationId} p={p} />)}
                    {nested.length > 0 && (
                      <details className="border-t border-gray-100">
                        <summary className="px-4 py-2 text-xs text-gray-500 cursor-pointer bg-gray-50">
                          Nested menus ({nested.length}) — already part of the locations above; save one only if it should also be its own location
                        </summary>
                        {nested.map(p => <ProposalRow key={p.locationId} p={p} />)}
                      </details>
                    )}
                  </div>
                )}
                <div className="mt-4 flex justify-between items-center">
                  <button onClick={() => setPhase({ kind: 'pick' })} className="text-xs text-gray-500 underline">Upload a different file</button>
                  <button onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">Done</button>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
